/**
 * Empty-state component with inline SVG illustrations.
 *
 * Variants:
 *   "no-data"   — generic, "nothing here yet"
 *   "search"    — no results for current query/filter
 *   "filter"    — no rows match the active filters
 *   "error"     — error fetching data
 *   "growth"    — no growth measurements
 *
 * The illustrations are inline SVG so the build stays self-contained
 * and the colors follow CSS vars (auto adapt to dark mode).
 */
import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import "./EmptyState.css";

const ILLUSTRATIONS = {
    "no-data": (
        <svg viewBox="0 0 200 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect x="32" y="36" width="136" height="84" rx="10" fill="var(--pt-surface-3)" />
            <rect x="48" y="56" width="104" height="10" rx="5" fill="var(--pt-border-strong)" />
            <rect x="48" y="76" width="72" height="8" rx="4" fill="var(--pt-border-default)" />
            <rect x="48" y="92" width="86" height="8" rx="4" fill="var(--pt-border-default)" />
            <circle cx="156" cy="48" r="14" fill="var(--pt-primary)" opacity="0.15" />
            <circle cx="156" cy="48" r="6" fill="var(--pt-primary)" />
        </svg>
    ),
    search: (
        <svg viewBox="0 0 200 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="86" cy="64" r="38" fill="var(--pt-surface-3)" />
            <circle cx="86" cy="64" r="26" fill="none" stroke="var(--pt-border-strong)" strokeWidth="6" />
            <line x1="106" y1="84" x2="138" y2="116" stroke="var(--pt-border-strong)" strokeWidth="8" strokeLinecap="round" />
        </svg>
    ),
    filter: (
        <svg viewBox="0 0 200 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect x="36" y="30" width="128" height="14" rx="7" fill="var(--pt-surface-3)" />
            <rect x="50" y="56" width="100" height="14" rx="7" fill="var(--pt-surface-3)" />
            <rect x="68" y="82" width="64" height="14" rx="7" fill="var(--pt-primary)" opacity="0.25" />
        </svg>
    ),
    error: (
        <svg viewBox="0 0 200 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="100" cy="70" r="48" fill="var(--pt-error)" opacity="0.12" />
            <line x1="100" y1="46" x2="100" y2="86" stroke="var(--pt-error)" strokeWidth="8" strokeLinecap="round" />
            <circle cx="100" cy="100" r="4.5" fill="var(--pt-error)" />
        </svg>
    ),
    growth: (
        <svg viewBox="0 0 200 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <line x1="24" y1="120" x2="180" y2="120" stroke="var(--pt-border-strong)" strokeWidth="2" />
            <polyline
                points="32,108 60,92 90,80 116,68 144,52 176,40"
                fill="none"
                stroke="var(--pt-primary)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {[32, 60, 90, 116, 144, 176].map((x, i) => (
                <circle key={i} cx={x} cy={[108, 92, 80, 68, 52, 40][i]} r="3.5" fill="var(--pt-primary)" />
            ))}
        </svg>
    ),
};

export function EmptyState({
    illustration = "no-data",
    title = "Nothing to show yet",
    description,
    action,
    compact = false,
}) {
    const art = ILLUSTRATIONS[illustration] || ILLUSTRATIONS["no-data"];
    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            className={`pt-empty-state ${compact ? "compact" : ""}`}
        >
            <Box className="pt-empty-state__art">{art}</Box>
            <Typography variant={compact ? "subtitle1" : "h5"} fontWeight={600} align="center">
                {title}
            </Typography>
            {description && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ maxWidth: 420 }}
                >
                    {description}
                </Typography>
            )}
            {action && <Box mt={1.5}>{action}</Box>}
        </Stack>
    );
}

export default EmptyState;
